/**
 * MyPay — each person's own pay page (Oct 19, 2026).
 *
 * Greg: "do we have this as a page or board for each individual, a way for
 * them to track their own pay … a money tab or pay tab for them to review.
 * keep track. … the 1/1 area … would be good for them to have on this page
 * too so they can play with those numbers themselves and dream and set goals."
 *
 *   • What was paid: this month, this quarter, the calendar year so far, from
 *     the stored payouts (commission_payments + paid SPIFFs) — lib/myPay.ts.
 *     Sales roles also see what is coming (pending approval + approved), from
 *     the same business snapshot as My Business.
 *   • The goal chart with a slider (sales roles): try any income, see the apps
 *     / claims / builds it takes every 30 days, save it as their goal.
 *   • Every statement: the monthly, quarterly and yearly statements and every
 *     pay day's commission and SPIFF statements, opened exactly as emailed.
 *
 * Finance and super users can pick anyone (?person=<id>) to see what that
 * person sees; the goal stays read-only for them here (the 1-on-1 is where
 * managers set goals).
 *
 * Oct 26, 2026 — a canvasser gets his own page under the same header
 * (CanvasserMyPay): hours, appointments, this week's estimate and every
 * week's pay. Canvassers aren't paid commissions or SPIFFs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import type { TeamMember } from "../lib/types";
import { fmtUSD } from "../lib/analytics";
import { FIELD_ROLES, todayET } from "../lib/businessSnapshot";
import { logCardClick } from "../lib/uiEvents";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { useBusinessSnapshot } from "../components/home/useBusinessSnapshot";
import CommissionGoalChart from "../components/snapshot/CommissionGoalChart";
import StatementViewer from "../components/commissions/StatementViewer";
import CanvasserMyPay from "../components/canvasser/CanvasserMyPay";
import {
  LEDGER_START,
  dayLabel,
  fetchPayEvents,
  fetchSentStatements,
  payDays,
  payYears,
  periodTotals,
  previewBody,
  type PayEvent,
  type PeriodTotal,
  type SentStatement,
  type StatementKind,
} from "../lib/myPay";

/** Who may look at someone else's pay here. */
const PICKER_ROLES = new Set(["admin", "finance", "sales_director", "director_of_operations"]);
/** Roles that are paid commissions, overrides, pool shares or SPIFFs (the picker's list). */
const PAID_ROLES = new Set(["restore_manager", "team_lead", "asm", "roof_specialist", "roof_team_manager", "sales_director", "admin", "director_of_operations", "canvasser"]);

const PERIOD_KIND_LABEL: Record<PeriodTotal["kind"], string> = { month: "Monthly", quarter: "Quarterly", year: "Annual" };

function Card({ title, sub, right, children }: { title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-bardin-ink">{title}</div>
          {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Tile({ label, value, detail, tone = "ink" }: { label: string; value: string; detail?: string; tone?: "ink" | "green" | "teal" }) {
  const cls = tone === "green" ? "text-emerald-600" : tone === "teal" ? "text-bardin-teal" : "text-bardin-ink";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-xl font-extrabold ${cls}`}>{value}</div>
      {detail && <div className="text-[10px] text-slate-400">{detail}</div>}
    </div>
  );
}

/** "Emailed Oct 16" — only for a statement that really went to the person (not a test-mode copy). */
function EmailedChip({ s }: { s: SentStatement | undefined }) {
  if (!s || s.status !== "sent" || s.mode === "test" || !s.sentAt) return null;
  const d = new Date(s.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">✉ emailed {d}</span>;
}

const parts = (commission: number, spiff: number) =>
  [commission ? `Commissions ${fmtUSD(commission)}` : null, spiff ? `SPIFFs ${fmtUSD(spiff)}` : null].filter(Boolean).join(" · ");

export default function MyPay() {
  const { member, role, isSuperUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const today = useMemo(() => todayET(), []);
  const canPick = !!isSuperUser || (!!role && PICKER_ROLES.has(role));
  const personId = (canPick && params.get("person")) || member?.id || null;
  const isSelf = !!member && personId === member.id;

  // The person whose pay this is (the viewer, or the one finance picked).
  const [roster, setRoster] = useState<TeamMember[]>([]);
  const [target, setTarget] = useState<TeamMember | null>(null);
  useEffect(() => {
    if (!personId) return;
    if (isSelf && member) { setTarget(member); return; }
    let cancelled = false;
    supabase.from("team_roster").select("*").eq("id", personId).maybeSingle().then(({ data }) => {
      if (!cancelled) setTarget((data as TeamMember | null) ?? null);
    });
    return () => { cancelled = true; };
  }, [personId, isSelf, member]);
  useEffect(() => {
    if (!canPick) return;
    supabase.from("team_roster").select("id, full_name, role, is_active").eq("is_active", true).order("full_name")
      .then(({ data }) => setRoster(((data ?? []) as TeamMember[]).filter((m) => PAID_ROLES.has(m.role))));
  }, [canPick]);

  // The ledger, and which statements were emailed.
  const [events, setEvents] = useState<PayEvent[] | null>(null);
  const [sent, setSent] = useState<Map<string, SentStatement>>(new Map());
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!personId) return;
    let cancelled = false;
    setErr(null);
    Promise.all([fetchPayEvents(personId), fetchSentStatements(personId).catch(() => new Map<string, SentStatement>())])
      .then(([ev, st]) => { if (!cancelled) { setEvents(ev); setSent(st); } })
      .catch((e: unknown) => { if (!cancelled) { setErr(e instanceof Error ? e.message : String(e)); setEvents([]); } });
    return () => { cancelled = true; };
  }, [personId, tick]);
  useEffect(() => { setEvents(null); }, [personId]);

  // New payouts land live (debounced: a Books pay run writes many rows at once).
  const bumpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bump = useCallback(() => {
    if (bumpTimer.current) clearTimeout(bumpTimer.current);
    bumpTimer.current = setTimeout(() => setTick((t) => t + 1), 1500);
  }, []);
  useEffect(() => () => { if (bumpTimer.current) clearTimeout(bumpTimer.current); }, []);
  useRealtimeTable("commission_payments", bump);
  useRealtimeTable("spiffs", bump);
  useRealtimeTable("commission_payment_emails", bump);

  // What is still coming (sales roles): the same snapshot as My Business.
  const isSales = !!target && FIELD_ROLES.has(target.role);
  const snap = useBusinessSnapshot(isSales ? target : null, role ?? null, !!isSuperUser);

  const years = useMemo(() => payYears(events ?? [], today), [events, today]);
  const yearParam = Number(params.get("year"));
  const year = years.includes(yearParam) ? yearParam : Number(today.slice(0, 4));
  const periods = useMemo(() => periodTotals(events ?? [], year, today), [events, year, today]);
  const days = useMemo(() => payDays(events ?? []).filter((d) => d.day.startsWith(String(year))), [events, year]);

  const thisYear = Number(today.slice(0, 4));
  const now = useMemo(() => {
    const all = periodTotals(events ?? [], thisYear, today);
    return {
      month: all.find((p) => p.kind === "month" && p.start === `${today.slice(0, 7)}-01`),
      quarter: all.find((p) => p.kind === "quarter" && p.start <= today && p.end >= today),
      year: all.find((p) => p.kind === "year"),
      payDays: payDays(events ?? []).filter((d) => d.day.startsWith(String(thisYear))).length,
    };
  }, [events, thisYear, today]);

  const [allDays, setAllDays] = useState(false);
  const [viewing, setViewing] = useState<{ title: string; kind: StatementKind; day: string } | null>(null);
  const open = (title: string, kind: StatementKind, day: string) => {
    logCardClick(`mypay_view_${kind}`);
    setViewing({ title, kind, day });
  };
  const closeViewer = useCallback(() => setViewing(null), []);

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  if (!member || !personId) return null;
  const first = (target?.full_name ?? member.full_name ?? "My").split(" ")[0];
  const shownPeriods = periods.filter((p) => p.count > 0);
  const ledgerYear = LEDGER_START.slice(0, 4) === String(year);
  const isCanvasser = target?.role === "canvasser";

  const header = (
    <>
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-bardin-ink">{isSelf ? "My Pay" : `${first}'s Pay`}</h1>
          <p className="text-sm text-slate-500">
            {isCanvasser ? "Your hours, appointments and every week's pay." : "Everything paid, every statement, and the goal you're working toward."}
          </p>
        </div>
        {canPick && (
          <select
            value={personId}
            onChange={(e) => setParam("person", e.target.value === member.id ? null : e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700"
            aria-label="Whose pay"
          >
            <option value={member.id}>Me ({member.full_name})</option>
            {roster.filter((m) => m.id !== member.id).map((m) => (
              <option key={m.id} value={m.id}>{m.full_name}</option>
            ))}
          </select>
        )}
      </div>
      {!isSelf && target && (
        <div className="mb-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800">
          You're looking at {target.full_name}'s pay as they see it.
          {isCanvasser ? "" : " Their goal can only be changed by them here, or by their manager at the 1-on-1."}
        </div>
      )}
    </>
  );

  if (isCanvasser) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        {header}
        <CanvasserMyPay memberId={personId} isSelf={isSelf} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      {header}

      {err && <div className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">Couldn't load the pay — {err}</div>}

      {/* What was paid */}
      <div className="mb-2 mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={`Paid in ${now.month?.label.split(" ")[0] ?? "this month"}`} value={events ? fmtUSD(now.month?.total ?? 0) : "…"} detail={events ? parts(now.month?.commission ?? 0, now.month?.spiff ?? 0) || "nothing yet" : undefined} tone="green" />
        <Tile label={`${now.quarter?.label.split(" ")[0] ?? "Quarter"} so far`} value={events ? fmtUSD(now.quarter?.total ?? 0) : "…"} detail={events ? parts(now.quarter?.commission ?? 0, now.quarter?.spiff ?? 0) || "nothing yet" : undefined} />
        <Tile label={`${thisYear} so far`} value={events ? fmtUSD(now.year?.total ?? 0) : "…"} detail={events ? parts(now.year?.commission ?? 0, now.year?.spiff ?? 0) || "nothing yet" : undefined} />
        {isSales ? (
          <Tile
            label="Coming"
            value={snap.core ? fmtUSD(snap.core.outlook.pendingQueue + snap.core.outlook.approvedAwaitingPay) : "…"}
            detail={snap.core ? `${fmtUSD(snap.core.outlook.approvedAwaitingPay)} approved · ${fmtUSD(snap.core.outlook.pendingQueue)} pending` : "loading"}
            tone="teal"
          />
        ) : (
          <Tile label="Pay days this year" value={events ? String(now.payDays) : "…"} />
        )}
      </div>
      {LEDGER_START.slice(0, 4) === String(thisYear) && (
        <p className="mb-4 text-[11px] text-slate-500">
          Commissions count from {dayLabel(LEDGER_START)}, {LEDGER_START.slice(0, 4)}, when the CRM began recording each payout. SPIFFs are complete.
          {isSales && isSelf && <> What's coming, projected and held is on <Link to="/my-business" className="font-semibold text-bardin-teal underline">My Business</Link>.</>}
        </p>
      )}

      {/* The goal, with the slider (sales roles) */}
      {isSales && target && (
        <div className="mb-4">
          <CommissionGoalChart memberId={target.id} member={target} core={snap.core} coreError={snap.error} selfEdit={isSelf} />
        </div>
      )}

      {years.length > 1 && (
        <div className="mb-3 flex gap-1">
          {years.map((y) => (
            <button key={y} onClick={() => setParam("year", y === thisYear ? null : String(y))}
              className={"rounded-lg px-3 py-1 text-sm font-semibold " + (y === year ? "bg-bardin-ink text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Monthly / quarterly / annual statements */}
      <Card title={`📄 ${year} statements`} sub="The monthly, quarterly and annual statements: every job and SPIFF in the period. Emailed on the 1st.">
        {events === null ? (
          <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
        ) : shownPeriods.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">Nothing paid in {year} yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {shownPeriods.map((p) => (
              <div key={`${p.kind}:${p.start}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                <div className="min-w-[10rem] flex-1">
                  <div className="text-sm font-medium text-bardin-ink">
                    {p.label}
                    <span className="ml-1.5 text-[11px] font-normal text-slate-400">{PERIOD_KIND_LABEL[p.kind]}{p.running ? " · so far" : ""}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">{parts(p.commission, p.spiff)}</div>
                </div>
                <EmailedChip s={sent.get(`${p.kind}:${p.start}`)} />
                <div className="text-sm font-bold text-bardin-ink">{fmtUSD(p.total)}</div>
                <button onClick={() => open(`${p.label} · ${PERIOD_KIND_LABEL[p.kind].toLowerCase()} statement`, p.kind, p.start)}
                  className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                  View
                </button>
              </div>
            ))}
          </div>
        )}
        {ledgerYear && shownPeriods.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
            Months before October {LEDGER_START.slice(0, 4)} show SPIFFs only: commissions paid before {dayLabel(LEDGER_START)} weren't recorded one by one.
          </div>
        )}
      </Card>

      {/* Every pay day */}
      <Card title={`💵 Pay days in ${year}`} sub="Each day you were paid, with that day's commission and SPIFF statements.">
        {events === null ? (
          <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
        ) : days.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">No pay days in {year} yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {(allDays ? days : days.slice(0, 12)).map((d) => (
              <div key={d.day} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                <div className="min-w-[10rem] flex-1">
                  <div className="text-sm font-medium text-bardin-ink">{dayLabel(d.day)}</div>
                  <div className="text-[11px] text-slate-500">
                    {[d.lines ? `${d.lines} commission line${d.lines === 1 ? "" : "s"} ${fmtUSD(d.commission)}` : null,
                      d.spiffs ? `${d.spiffs} SPIFF${d.spiffs === 1 ? "" : "s"} ${fmtUSD(d.spiff)}` : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="text-sm font-bold text-bardin-ink">{fmtUSD(d.total)}</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.lines > 0 && (
                    <span className="flex items-center gap-1">
                      <EmailedChip s={sent.get(`commission:${d.day}`)} />
                      <button onClick={() => open(`Commission statement · ${dayLabel(d.day)}`, "commission", d.day)}
                        className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                        Commissions
                      </button>
                    </span>
                  )}
                  {d.spiffs > 0 && (
                    <span className="flex items-center gap-1">
                      <EmailedChip s={sent.get(`spiff:${d.day}`)} />
                      <button onClick={() => open(`SPIFF statement · ${dayLabel(d.day)}`, "spiff", d.day)}
                        className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                        SPIFFs
                      </button>
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!allDays && days.length > 12 && (
              <button onClick={() => setAllDays(true)} className="w-full px-4 py-2.5 text-center text-xs font-semibold text-bardin-teal hover:bg-slate-50">
                Show all {days.length} pay days
              </button>
            )}
          </div>
        )}
      </Card>

      <p className="text-[11px] text-slate-400">
        Questions about a payment? Reply to any statement email (it goes to Shannon in finance) or call (470) 823-8816.
      </p>

      {viewing && (
        <StatementViewer
          title={viewing.title}
          body={previewBody(personId, viewing.kind, viewing.day)}
          onClose={closeViewer}
        />
      )}
    </div>
  );
}
